import React, { createContext, useContext, useState, useEffect } from 'react';
import { string, bool, node } from 'prop-types';
import { useStore, useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { useDataModel } from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { Skeleton, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { ApplianceBundlesDataModel } from './ApplianceBundlesDataModel';
import { structureBundle, getCart } from '../utils';

const ApplianceBundlesContext = createContext({
  anchorItem: {},
  groupedBundleItems: {},
  accessoryItems: [],
  headings: {
    entireBundleHeading: 'Appliance Bundle',
    accessoryHeading: 'Accessories',
  },
  isMobile: false,
  isConsumerApp: false
});
export const useApplianceBundles = () => useContext(ApplianceBundlesContext);

export const ApplianceBundlesProvider = ({
  children, itemId, isB2BCustomer, isServer, channel, isConsumerApp
}) => {
  const { storeId } = useStore();
  const experienceContext = useContext(ExperienceContext);
  const deliveryZip = experienceContext?.deliveryZip || '';
  const enableApplianceDeliveryCharge = useConfigService('enableApplianceDeliveryCharge') || false;
  const enableFreeDeliveryForExchange = useConfigService('enableFreeDeliveryForExchange') || false;
  const enableAssortmentStrategyDiscontinued = useConfigService('enableAssortmentStrategyDiscontinued') || false;
  const applianceDeliveryChargeValue = useConfigService('applianceDeliveryChargeValue') || 29;
  const {
    isCustomerIdentified,
    isExchangeCustomer
  } = useThdCustomer() || {};

  const [selectedItems, setSelectedItems] = useState([]);

  const [anchorItem, setAnchorItem] = useState({});
  const [groupedBundleItems, setGroupedBundleItems] = useState({});
  const [accessoryItems, setAccessoryItems] = useState({});

  const [headings, setHeadings] = useState({
    entireBundleHeading: '',
    accessoryHeading: ''
  });

  const [bundleType, setBundleType] = useState('default');

  const {
    data: bundleData,
    loading: bundleLoading,
    error: bundleError
  } = useDataModel('bundle', {
    variables: {
      itemId,
      storeId
    },
    ssr: true
  });

  const {
    data: clientProductData,
    loading: clientProductLoading
  } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    ssr: false
  });

  const {
    data: productData,
    loading: productLoading
  } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: (!isServer && storeId !== '8119')
  });

  const bundleProductIds = bundleData?.bundle?.products?.map(
    (productMap) => productMap.product?.itemId).filter(Boolean) || [itemId];

  const {
    data: availabilityData,
    loading: availabilityLoading,
    error: availabilityError
  } = useDataModel('checkAvailability', {
    variables: {
      itemIds: bundleProductIds,
      zipCode: deliveryZip,
      localStoreId: storeId
    },
    ssr: false,
    skip: !enableAssortmentStrategyDiscontinued || !deliveryZip || !storeId || !bundleProductIds.length
  });

  let hasDiscontinued = false;

  if (enableAssortmentStrategyDiscontinued) {
    hasDiscontinued = availabilityData?.checkAvailability?.fulfillmentOptions?.some(
      (option) => option?.services?.some(
        (service) => service?.deliveryAvailabilityDetail?.some(
          (detail) => detail?.assortmentStrategy === 'Inactive Discontinue'
        )
      )
    );
  }

  const updateItems = ({ oldItem = {}, newItem = {} }) => {
    setSelectedItems([...selectedItems.filter((item) => item.product.itemId !== oldItem.product.itemId), newItem]);
  };

  const addItem = ({ addedItem = {} }) => {
    setSelectedItems([...selectedItems, addedItem]);
  };

  const removeItem = ({ removedItem = {} }) => {
    setSelectedItems([...selectedItems.filter((item) => item.product.itemId !== removedItem.product.itemId)]);
  };

  const handleDynamicLoad = () => {
    const {
      anchorItem: anchorItemInitial,
      groupedBundleItems: groupedBundleItemsInitial,
      accessoryItems: accessoryItemsInitial,
      entireBundleHeading,
      accessoryHeading,
      bundleType: bundleTypeInitial
    } = structureBundle(bundleData, clientProductData, productData);

    setAnchorItem(anchorItemInitial);
    setGroupedBundleItems(groupedBundleItemsInitial);
    setAccessoryItems(accessoryItemsInitial);
    setHeadings({
      entireBundleHeading,
      accessoryHeading
    });
    setBundleType(bundleTypeInitial);

    const initialSelectedItems = [
      anchorItemInitial,
      ...Object.keys(groupedBundleItemsInitial).map((productGroup) => groupedBundleItemsInitial[productGroup][0])
    ];
    if (accessoryItemsInitial.length) {
      initialSelectedItems.push(accessoryItemsInitial[0]);
    }

    setSelectedItems(initialSelectedItems);
  };

  useEffect(() => {
    if (bundleData?.bundle?.products?.length && !hasDiscontinued) {
      handleDynamicLoad();
    }
  }, [bundleData]);

  if (
    bundleData?.bundle?.products?.length === 0
    || bundleData?.bundle?.products === null
    || bundleData?.bundle === null
    || !!bundleError
    || hasDiscontinued
  ) {
    return null;
  }
  if (bundleLoading || clientProductLoading || productLoading || availabilityLoading) {
    return (
      <Skeleton disablePadding orientation="horizontal">
        <SkeletonBlock height="400px" width="800px" />
      </Skeleton>
    );
  }

  const { cartOptions, cartReqParams } = getCart(channel, selectedItems);

  return (
    <ApplianceBundlesContext.Provider value={{
      cartOptions,
      cartReqParams,
      selectedItems,
      updateItems,
      addItem,
      removeItem,
      anchorItem,
      groupedBundleItems,
      accessoryItems,
      entireBundleHeading: headings.entireBundleHeading,
      accessoryHeading: headings.accessoryHeading,
      bundleType,
      hasBundleItems: Object.keys(groupedBundleItems).length > 0,
      hasAccessoryItems: !!accessoryItems.length,
      isB2BCustomer,
      itemId,
      storeId,
      hasMobileView: channel === 'mobile' || isConsumerApp,
      hasTabletView: channel === 'tablet',
      channel,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryChargeValue,
      isCustomerIdentified,
      isExchangeCustomer,
      metadata: bundleData?.bundle?.metadata
    }}
    >
      {children}
    </ApplianceBundlesContext.Provider>
  );
};

ApplianceBundlesProvider.propTypes = {
  children: node.isRequired,
  itemId: string.isRequired,
  isB2BCustomer: bool.isRequired,
  isServer: bool.isRequired,
  channel: string.isRequired,
  isConsumerApp: bool.isRequired
};

ApplianceBundlesProvider.dataModel = ApplianceBundlesDataModel;
